import {
  Card,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@heroui/react'

import type {
  EvalMode,
  ModelSummary,
  ReportJson,
} from '../../../../../functions/tests/eval/phase1/types'

import { fmtCost, fmtMs, fmtPct } from './formatters'

function ranking(summaries: ModelSummary[]): ModelSummary[] {
  return [...summaries].sort((a, b) => {
    if (b.cleanCount !== a.cleanCount) return b.cleanCount - a.cleanCount
    return a.avgLatencyMs - b.avgLatencyMs
  })
}

interface LeaderboardTableProps {
  data: ReportJson
  mode: EvalMode
  title: string
}

export function LeaderboardTable({ data, mode, title }: LeaderboardTableProps) {
  const summariesForMode = data.summaries.filter((s) => s.mode === mode)
  if (summariesForMode.length === 0) return null
  const ranked = ranking(summariesForMode)

  return (
    <Card className="p-4 space-y-3">
      <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
        {title}
      </h2>
      <Table aria-label={title} removeWrapper>
        <TableHeader>
          <TableColumn>Model</TableColumn>
          <TableColumn>Clean</TableColumn>
          <TableColumn>Turn ✓</TableColumn>
          <TableColumn>UserID ✓</TableColumn>
          <TableColumn>Speaker Acc</TableColumn>
          <TableColumn>Avg Latency</TableColumn>
          <TableColumn>Avg Cost</TableColumn>
          <TableColumn>Errors</TableColumn>
        </TableHeader>
        <TableBody>
          {ranked.map((s, i) => {
            const model = data.models.find((m) => m.key === s.modelKey)
            const display = model?.displayName ?? s.modelKey
            const isWinner = i === 0
            return (
              <TableRow key={`${s.modelKey}-${s.mode}`}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {isWinner && (
                      <Chip size="sm" color="success" variant="flat">
                        best
                      </Chip>
                    )}
                    <span className="font-medium">{display}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <span
                    className={
                      s.cleanCount === s.total
                        ? 'text-success-600 dark:text-success-400 font-medium'
                        : ''
                    }
                  >
                    {s.cleanCount}/{s.total}
                  </span>
                </TableCell>
                <TableCell>
                  {s.turnCountMatchCount}/{s.total}
                </TableCell>
                <TableCell>
                  {s.userPresenceMatchCount}/{s.total}
                </TableCell>
                <TableCell>{fmtPct(s.avgSpeakerAccuracy)}</TableCell>
                <TableCell>{fmtMs(s.avgLatencyMs)}</TableCell>
                <TableCell>{fmtCost(s.avgCostUsd)}</TableCell>
                <TableCell>
                  {s.errorCount > 0 ? (
                    <Chip size="sm" color="danger" variant="flat">
                      {s.errorCount}
                    </Chip>
                  ) : (
                    <span className="text-slate-400">0</span>
                  )}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </Card>
  )
}
